import { useEffect, useState } from 'react';
import MediaAPI from '../../../api/media';
import { isRecord } from '../../../api/typeHelpers';
import { SlideTag, readSlideTag, renderOutputMediaId } from './slideOutputs';

export type SlideTags = Record<string, SlideTag | undefined>;

// One lookup per media id for the page's lifetime, shared by every mounted
// strip. A render output's slide tag is written once, when the output is
// stored, and never changes, so re-rendering a strip or expanding its clip
// again must not fire a fresh GET per slide. Concurrent strips showing the same
// output share one in-flight request. A failed lookup is not remembered: the
// output shows as an untagged tile, and the next mount asks again.
const known = new Map<string, SlideTag | undefined>();
const inflight = new Map<string, Promise<SlideTag | undefined>>();
// Bumped by a reset, so a lookup started before it cannot refill the cache.
let generation = 0;

/** How long the strip waits for one lookup before showing that output untagged.
 *  GET /media/:id can download the whole file first on a cold backend. */
export const SLIDE_TAG_TIMEOUT_MS = 8000;

const fetchSlideTag = async (mediaId: string): Promise<SlideTag | undefined> => {
  const item: unknown = await MediaAPI.getMediaItem(mediaId);
  // A 200 that is not a media item (a deploy's index.html fallback) is a
  // failed lookup, not "no tag": it must not be remembered.
  if (!isRecord(item)) throw new Error('not a media item');
  return readSlideTag(item.metadata) ?? undefined;
};

const lookUpSlideTag = (mediaId: string): Promise<SlideTag | undefined> => {
  if (known.has(mediaId)) return Promise.resolve(known.get(mediaId));
  const shared = inflight.get(mediaId);
  if (shared) return shared;
  const started = generation;
  const pending: Promise<SlideTag | undefined> = fetchSlideTag(mediaId)
    .then((tag) => {
      if (started === generation) known.set(mediaId, tag);
      return tag;
    })
    .finally(() => {
      if (inflight.get(mediaId) === pending) inflight.delete(mediaId);
    });
  inflight.set(mediaId, pending);
  return pending;
};

/** The lookup, or a failure after SLIDE_TAG_TIMEOUT_MS. The request itself
 *  keeps going, so a late answer still serves the next mount. */
const lookUpSlideTagInTime = (mediaId: string): Promise<SlideTag | undefined> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('slide tag lookup timed out')), SLIDE_TAG_TIMEOUT_MS);
    lookUpSlideTag(mediaId).then(
      (tag) => {
        clearTimeout(timer);
        resolve(tag);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

/** Every URL's tag when all of them were looked up before; null otherwise. */
const knownSlideTags = (urls: string[]): SlideTags | null => {
  const tags: SlideTags = {};
  for (const url of urls) {
    const mediaId = renderOutputMediaId(url);
    if (mediaId && !known.has(mediaId)) return null;
    tags[url] = mediaId ? known.get(mediaId) : undefined;
  }
  return tags;
};

/** Test-only: the lookups above are module-level and otherwise outlive every test. */
export const __resetSlideTagCache = (): void => {
  generation += 1;
  known.clear();
  inflight.clear();
};

/**
 * The slide tag of every render output URL, read from its media item
 * (GET /api/media/:id). null until every lookup has settled or timed out. A
 * URL that is not a managed upload, whose lookup failed or timed out, or whose
 * item carries no tag maps to undefined, and the strip shows it as an untagged
 * tile.
 */
export const useSlideTags = (urls: string[]): SlideTags | null => {
  // The effect keys on the URL list's content, not its identity: a parent
  // re-render that hands over an equal fresh array must not look anything up.
  const key = urls.join('\n');
  const [loaded, setLoaded] = useState<{ key: string; tags: SlideTags } | null>(() => {
    const tags = knownSlideTags(urls);
    return tags ? { key, tags } : null;
  });

  useEffect(() => {
    const list = key ? key.split('\n') : [];
    const ready = knownSlideTags(list);
    if (ready) {
      setLoaded((previous) => (previous?.key === key ? previous : { key, tags: ready }));
      return undefined;
    }
    let active = true;
    void Promise.all(
      list.map(async (url): Promise<[string, SlideTag | undefined]> => {
        const mediaId = renderOutputMediaId(url);
        if (!mediaId) return [url, undefined];
        try {
          return [url, await lookUpSlideTagInTime(mediaId)];
        } catch {
          return [url, undefined];
        }
      })
    ).then((entries) => {
      if (active) setLoaded({ key, tags: Object.fromEntries(entries) });
    });
    return () => {
      active = false;
    };
  }, [key]);

  return loaded && loaded.key === key ? loaded.tags : null;
};
